import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import { supabase } from "../../../utils/supabase";
import { useLabPermissions } from "../../../hooks/useLabPermissions";
import { drawFlowingObservations } from "../../../utils/in-situ-pdf-layout";

function formatDateEU(dateStr) {
  const calendarDate = String(dateStr || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (calendarDate) {
    return `${calendarDate[3]}/${calendarDate[2]}/${calendarDate[1]}`;
  }
  const d = dateStr ? new Date(dateStr) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function toDateInputValue(value) {
  if (value) {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function digitsOnly(v) {
  return String(v || "").replace(/\D/g, "");
}

function resolveBaseNumber(po) {
  if (po?.base_number != null && String(po.base_number).trim() !== "") {
    return digitsOnly(po.base_number);
  }
  const codeDigits = digitsOnly(po?.code || "");
  if (codeDigits) return codeDigits.slice(0, Math.max(1, codeDigits.length - 3));
  return "";
}

function extractSeqFromCode(code) {
  const s = String(code || "");
  const m = s.match(/(\d{3})\s*$/);
  if (!m) return null;
  const n = Number.parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
}

function normalizeMapsUrl(rawUrl) {
  const url = String(rawUrl || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function drawPageFrame(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setLineWidth(0.35);
  doc.rect(6, 6, pageWidth - 12, pageHeight - 12);
}

async function loadImageDataUrl(url) {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`No se pudo cargar imagen: ${url}`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function getImageNaturalSize(dataUrl) {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function drawLabeledRow(doc, x, y, w, h, label, value, splitX) {
  doc.setLineWidth(0.2);
  doc.rect(x, y, w, h);
  doc.line(x + splitX, y, x + splitX, y + h);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(`${label}:`, x + 2, y + h - 2);

  doc.setFont("helvetica", "normal");
  const maxW = w - splitX - 4;
  const rowText = String(value || "");
  const clipped = rowText.length ? doc.splitTextToSize(rowText, maxW)[0] : "";
  doc.text(clipped, x + splitX + 2, y + h - 2);
}

function drawFooterSignatures(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const footerX = 10;
  const footerY = pageHeight - 58;
  const footerW = pageWidth - 20;
  const footerH = 46;
  const colW = footerW / 2;
  const labelW = 30;
  const headerH = 8;
  const rowH = 7;
  const signH = footerH - headerH - rowH * 3;

  const drawBlock = (x, title) => {
    doc.setLineWidth(0.25);
    doc.rect(x, footerY, colW, footerH);

    // Header
    doc.setFillColor(240, 240, 240);
    doc.rect(x, footerY, colW, headerH, "F");
    doc.rect(x, footerY, colW, headerH);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(title, x + 3, footerY + 5.5);

    // Nombre
    let y = footerY + headerH;
    doc.rect(x, y, labelW, rowH);
    doc.rect(x + labelW, y, colW - labelW, rowH);
    doc.text("Nombre", x + 2, y + 4.7);

    // Sector
    y += rowH;
    doc.rect(x, y, labelW, rowH);
    doc.rect(x + labelW, y, colW - labelW, rowH);
    doc.text("Sector", x + 2, y + 4.7);

    // Fecha
    y += rowH;
    doc.rect(x, y, labelW, rowH);
    doc.rect(x + labelW, y, colW - labelW, rowH);
    doc.text("Fecha", x + 2, y + 4.7);

    // Firma y aclaracion
    y += rowH;
    doc.rect(x, y, labelW, signH);
    doc.rect(x + labelW, y, colW - labelW, signH);
    doc.text("Firma y", x + 2, y + 4.6);
    doc.text("Aclaracion", x + 2, y + 8.8);
  };

  drawBlock(footerX, "Por CALINS S.A.");
  drawBlock(footerX + colW, "Por el Cliente");
}

export default function InSituOSButton({ po }) {
  const { isAdmin } = useLabPermissions();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("15");
  const [observations, setObservations] = useState("");
  const [operators, setOperators] = useState("");
  const [entryHour, setEntryHour] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [serviceAddress, setServiceAddress] = useState("");
  const [mapsLink, setMapsLink] = useState("");
  const [documentDate, setDocumentDate] = useState(() => toDateInputValue(po?.created_at));
  const [existingCount, setExistingCount] = useState(0);
  const [suggestedStartSeq, setSuggestedStartSeq] = useState(1);
  const [loading, setLoading] = useState(false);

  if (!po) return null;

  const closeModal = () => {
    if (loading) return;
    setOpen(false);
  };

  const openModal = async () => {
    try {
      setDocumentDate(toDateInputValue(po?.created_at));
      setOpen(true);
      const { data, error } = await supabase
        .from("v_service_orders_enriched")
        .select("code")
        .eq("po_id", po.id);
      if (error) throw error;

      const rows = data || [];
      const count = rows.length;
      let maxSeq = 0;
      for (const r of rows) {
        const seq = extractSeqFromCode(r?.code);
        if (seq != null && seq > maxSeq) maxSeq = seq;
      }
      const baseFromCount = count + 1;
      const baseFromSeq = maxSeq + 1;
      const baseFromPo = Number.isInteger(po?.os_start_seq) ? po.os_start_seq : 1;
      const next = Math.max(baseFromPo, baseFromCount, baseFromSeq);

      setExistingCount(count);
      setSuggestedStartSeq(next);
    } catch (e) {
      console.error("[InSituOSButton] load existing count", e);
      setExistingCount(0);
      setSuggestedStartSeq(Number.isInteger(po?.os_start_seq) ? po.os_start_seq : 1);
    }
  };

  const handleGenerate = async () => {
    const total = Number.parseInt(String(qty || "").trim(), 10);
    if (!Number.isInteger(total) || total <= 0) {
      alert("Cantidad invalida. Ingresa un numero mayor a 0.");
      return;
    }
    if (total > 500) {
      alert("Cantidad demasiado alta. Maximo permitido: 500.");
      return;
    }
    if (isAdmin && !/^\d{4}-\d{2}-\d{2}$/.test(documentDate)) {
      alert("Selecciona una fecha valida para el documento.");
      return;
    }

    const resolvedServiceAddress = String(serviceAddress || "").trim();
    const resolvedMapsLink = normalizeMapsUrl(mapsLink);
    if (!resolvedServiceAddress) {
      alert("Ingresa la direccion del servicio.");
      return;
    }
    if (!resolvedMapsLink) {
      alert("Ingresa el link de Google Maps.");
      return;
    }

    try {
      setLoading(true);

      let client = null;
      if (po.client_id) {
        const { data, error } = await supabase
          .from("clients")
          .select("id,name,contact,phone")
          .eq("id", po.client_id)
          .single();
        if (!error) client = data;
      }

      const baseNumber = resolveBaseNumber(po);
      const startSeq = suggestedStartSeq;
      const osLabel = (po?.code || (baseNumber ? `OS-${baseNumber}` : "OS")).replace(/^OC-/i, "OS-");
      const resolvedContactName = contactName.trim() || client?.contact || "-";
      const resolvedContactPhone = contactPhone.trim() || client?.phone || "-";

      const doc = new jsPDF("p", "mm", "a4");
      drawPageFrame(doc);

      const pageWidth = doc.internal.pageSize.getWidth();

      try {
        const logoDataUrl = await loadImageDataUrl("/logo-calins-color.png");
        const { w, h } = await getImageNaturalSize(logoDataUrl);
        const targetW = 34;
        const ratio = h / w;
        const logoH = targetW * ratio;
        doc.addImage(logoDataUrl, "PNG", pageWidth - 44, 8, targetW, logoH);
      } catch (e) {
        console.warn("[InSituOSButton] logo", e);
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Documento de servicio In Situ", pageWidth / 2, 14, { align: "center" });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`OS/OC: ${osLabel}`, 10, 21);
      const resolvedDocumentDate = isAdmin
        ? documentDate
        : toDateInputValue(po.created_at);
      doc.text(`Fecha: ${formatDateEU(resolvedDocumentDate)}`, pageWidth - 10, 21, {
        align: "right",
      });

      const headerX = 10;
      const headerY = 24;
      const headerW = pageWidth - 20;
      const headerH = 32;
      const splitX = 26;

      const rows = [
        ["Cliente", client?.name || po.client_name || "-"],
        ["Contacto", resolvedContactName],
        ["Telefono", resolvedContactPhone],
        ["Operarios", operators.trim() || "-"],
      ];

      const rowH = headerH / rows.length;
      rows.forEach((r, idx) => {
        drawLabeledRow(doc, headerX, headerY + idx * rowH, headerW, rowH, r[0], r[1], splitX);
      });

      const serviceY = headerY + headerH + 3;
      const serviceH = 32;
      const serviceLeftW = 116;
      const serviceRightW = headerW - serviceLeftW - 4;
      const serviceLeftX = headerX;
      const serviceRightX = serviceLeftX + serviceLeftW + 4;

      doc.setLineWidth(0.25);
      doc.rect(serviceLeftX, serviceY, serviceLeftW, serviceH);
      doc.rect(serviceRightX, serviceY, serviceRightW, serviceH);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Direccion del servicio", serviceLeftX + 2, serviceY + 4.5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      const serviceLines = doc.splitTextToSize(resolvedServiceAddress, serviceLeftW - 4).slice(0, 2);
      serviceLines.forEach((line, idx) => {
        doc.text(line, serviceLeftX + 2, serviceY + 10 + idx * 4.2);
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Google Maps", serviceRightX + 2, serviceY + 4.5);
      const qrDataUrl = await QRCode.toDataURL(resolvedMapsLink, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 240,
      });
      const qrSize = 20;
      const qrX = serviceRightX + (serviceRightW - qrSize) / 2;
      doc.addImage(qrDataUrl, "PNG", qrX, serviceY + 8, qrSize, qrSize);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      const linkPreview =
        resolvedMapsLink.length > 42 ? `${resolvedMapsLink.slice(0, 39)}...` : resolvedMapsLink;
      doc.text(linkPreview, serviceRightX + 2, serviceY + serviceH - 2, {
        maxWidth: serviceRightW - 4,
      });

      const obsY = serviceY + serviceH + 2;
      const scheduleY = drawFlowingObservations(doc, {
        text: observations,
        startY: obsY,
        x: 10,
        width: pageWidth - 20,
        drawPageFrame,
      });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Hora entrada:", 10, scheduleY + 4);
      doc.setFont("helvetica", "normal");
      doc.setLineWidth(0.25);
      doc.rect(30, scheduleY, 24, 6);
      if (entryHour.trim()) doc.text(entryHour.trim(), 32, scheduleY + 4.3);

      doc.setFont("helvetica", "bold");
      doc.text("Hora salida:", 62, scheduleY + 4);
      doc.setFont("helvetica", "normal");
      doc.rect(81, scheduleY, 24, 6);

      let startY = scheduleY + 10;
      const tableBottomLimit = doc.internal.pageSize.getHeight() - 62;
      const minimumTableSpace = 16;
      if (startY + minimumTableSpace > tableBottomLimit) {
        doc.addPage();
        drawPageFrame(doc);
        startY = 12;
      }
      const head = [["Cert N", "Tipo de instrumento", "Estado", "Numero de serie", "Tag"]];
      const body = Array.from({ length: total }).map((_, idx) => {
        const seq = String(startSeq + idx).padStart(3, "0");
        const cert = baseNumber ? `${baseNumber} ${seq}` : seq;
        return [cert, "", "", "", ""];
      });

      autoTable(doc, {
        head,
        body,
        startY,
        theme: "grid",
        margin: { left: 10, right: 10, bottom: 62 },
        styles: {
          fontSize: 8,
          cellPadding: 1.4,
          lineWidth: 0.2,
          lineColor: [0, 0, 0],
          minCellHeight: 6,
          valign: "middle",
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [20, 20, 20],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 84 },
          2: { cellWidth: 24 },
          3: { cellWidth: 32 },
          4: { cellWidth: 20 },
        },
        didDrawPage: () => {
          drawPageFrame(doc);
        },
      });

      const totalPages = doc.getNumberOfPages();
      doc.setPage(totalPages);
      drawFooterSignatures(doc);

      const safeBase = baseNumber || "sin-base";
      doc.save(`OS-InSitu-${safeBase}.pdf`);
      setOpen(false);
    } catch (e) {
      console.error("[InSituOSButton] error:", e);
      alert(e.message || "No se pudo generar el PDF DS PDF.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="px-2 py-1.5 rounded-md border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 disabled:opacity-60"
        onClick={openModal}
        disabled={loading}
        title="Generar planilla DS PDF en PDF (A4 vertical)"
      >
        {loading ? "Generando..." : "DS PDF"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4"
          onMouseDown={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div
            className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl border border-white/10 bg-slate-950 p-5 shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="text-lg font-semibold text-white">Planilla DS PDF</div>
            <div className="mt-1 text-sm text-white/70">
              Define la cantidad de instrumentos y observaciones para generar el PDF en blanco.
            </div>
            <div className="mt-2 text-xs text-sky-200/90">
              En esta OC ya hay <b>{existingCount}</b> instrumentos. El primer correlativo DS PDF sera{" "}
              <b>{String(suggestedStartSeq).padStart(3, "0")}</b>.
            </div>

            <label className="mt-4 block text-sm text-white/80">Cantidad de instrumentos</label>
            <input
              type="number"
              min="1"
              max="500"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
              placeholder="Ej: 15"
            />

            {isAdmin && (
              <div className="mt-4">
                <label className="block text-sm text-white/80">Fecha del documento</label>
                <input
                  type="date"
                  value={documentDate}
                  onChange={(e) => setDocumentDate(e.target.value)}
                  className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
                />
                <p className="mt-1 text-xs text-white/50">
                  Esta es la fecha que se imprimirá en el encabezado del DS PDF.
                </p>
              </div>
            )}

            <label className="mt-4 block text-sm text-white/80">Operarios que realizan el servicio</label>
            <input
              type="text"
              value={operators}
              onChange={(e) => setOperators(e.target.value)}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
              placeholder="Ej: Juan Perez, Ana Lopez"
            />

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-white/80">Hora de entrada</label>
                <input
                  type="text"
                  value={entryHour}
                  onChange={(e) => setEntryHour(e.target.value)}
                  className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
                  placeholder="Ej: 08:30"
                />
              </div>
              <div>
                <label className="block text-sm text-white/80">Hora de salida (manual en papel)</label>
                <input
                  type="text"
                  disabled
                  className="mt-1 w-full rounded-md border border-white/10 bg-black/40 px-3 py-2 text-white/60"
                  placeholder="Se completa a mano"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-white/80">Nombre de contacto</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
                  placeholder="Opcional (sobrescribe contacto del cliente)"
                />
              </div>
              <div>
                <label className="block text-sm text-white/80">Telefono de contacto</label>
                <input
                  type="text"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
                  placeholder="Opcional (sobrescribe telefono del cliente)"
                />
              </div>
            </div>

            <label className="mt-4 block text-sm text-white/80">Direccion del servicio</label>
            <textarea
              rows={2}
              value={serviceAddress}
              onChange={(e) => setServiceAddress(e.target.value)}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
              placeholder="Direccion donde se hara el servicio in situ"
            />

            <label className="mt-4 block text-sm text-white/80">Link de Google Maps</label>
            <input
              type="text"
              value={mapsLink}
              onChange={(e) => setMapsLink(e.target.value)}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
              placeholder="Pegue el link de Google Maps"
            />

            <label className="mt-4 block text-sm text-white/80">Observaciones</label>
            <textarea
              rows={3}
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-white/30"
              placeholder="Texto opcional para la planilla DS PDF..."
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-white/15 px-3 py-2 text-white/80 hover:bg-white/10"
                onClick={closeModal}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rounded-md border border-fuchsia-500/60 px-3 py-2 text-fuchsia-200 hover:bg-fuchsia-500/10"
                onClick={handleGenerate}
                disabled={loading}
              >
                {loading ? "Generando..." : "Generar PDF DS PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
